import React, { useState } from "react";
import { X, CheckCircle2 } from "lucide-react";
import CvImportStep from "@/components/admin/recruitment/cv-builder/CvImportStep";
import { useCvImportPipeline } from "@/hooks/useCvImportPipeline";

const UnifiedCvImportModal = ({
    isOpen,
    onClose,
    title = "Importer un CV via IA",
    onSuccess,
    onOpenBuilder,
    storageKey = "lenvia_active_parse_job",
}) => {
    const [completedDraft, setCompletedDraft] = useState(null);

    const pipeline = useCvImportPipeline({
        storageKey,
        onSuccess: (result, file) => {
            if (result?.draft) {
                setCompletedDraft(result.draft);
            }
            if (onSuccess) {
                onSuccess(result, file);
            }
        },
    });

    if (!isOpen) return null;

    const handleClose = () => {
        pipeline.reset();
        setCompletedDraft(null);
        onClose();
    };

    const draftId = completedDraft?.id || completedDraft?._id;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
            data-testid="unified-cv-import-modal"
        >
            <div
                className="relative w-full max-w-2xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col text-left"
                role="dialog"
                aria-modal="true"
                aria-labelledby="import-modal-title"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                    <h3 id="import-modal-title" className="text-lg font-bold text-white">
                        {title}
                    </h3>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={pipeline.isLoading}
                        className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/5 disabled:opacity-50"
                        aria-label="Fermer la modal d'import"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto">
                    {completedDraft ? (
                        <div className="text-center py-6 space-y-4">
                            <div className="w-14 h-14 rounded-full bg-emerald-500/20 text-emerald-400 mx-auto flex items-center justify-center">
                                <CheckCircle2 className="w-8 h-8" />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-white">
                                    CV importé avec succès !
                                </h4>
                                <p className="text-sm text-slate-300 mt-1">
                                    Le document a été analysé, normalisé et enregistré dans votre bibliothèque.
                                </p>
                            </div>

                            <div className="pt-4 flex flex-wrap justify-center gap-3">
                                {onOpenBuilder && draftId && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleClose();
                                            onOpenBuilder(draftId);
                                        }}
                                        className="px-5 py-2.5 rounded-xl bg-lenvia-blue hover:bg-blue-600 text-white text-xs font-bold transition-all shadow-md"
                                        data-testid="btn-open-imported-builder"
                                    >
                                        Ouvrir dans le CV Builder
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-slate-200 text-xs font-semibold transition-all"
                                >
                                    Rester dans la bibliothèque
                                </button>
                            </div>
                        </div>
                    ) : (
                        <CvImportStep
                            onFileSelected={(file) => pipeline.startImport(file)}
                            isLoading={pipeline.isLoading}
                            importProgress={pipeline.importProgress}
                            importError={pipeline.importError}
                            importWarnings={pipeline.importWarnings}
                            lastImportResult={pipeline.lastImportResult}
                            onRetry={pipeline.retry}
                            onOpenBuilder={onOpenBuilder ? (id) => onOpenBuilder(id || draftId) : null}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default UnifiedCvImportModal;
